import React from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";

import { Controller, useForm } from "react-hook-form";
import { signOut, useCompleteSignup } from "api/auth";
import ImageInput from "generics/ImageInput";
import Map from "generics/Map";
import { LogoutIcon } from "assets/svg/LogoutIcon";
import logoWithName from "assets/logo_with_name.png";

export default function CompleteSignUp() {
  const { register, handleSubmit, errors, trigger, control } = useForm({
    defaultValues: {
      opensAt: "10:00",
      closesAt: "18:00",
      location: null,
      locationId: null,
    },
  });

  const toast = useToast();

  const [completeSignup, completeSignupInfo] = useCompleteSignup();

  async function onSubmit(values: any) {
    try {
      if (
        Array.from(values.verificationDocuments).find(
          (i: any) => i.size > 5 * 1024 * 1024
        )
      ) {
        toast({
          status: "error",
          title: "Error",
          description: "Files must be less than 5MB in size!",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!values.createdAt) {
        values.createdAt = Date.now();
      }
      values.updatedAt = Date.now();
      values.locationIds = [];
      await completeSignup(values);
    } catch (e) {
      console.error("error in onsubmit in sign up", e);
    }
  }

  return (
    <Flex
      minH="100vh"
      justify="center"
      align="center"
      direction="column"
      bgColor="#f5f6fa"
    >
      <Flex
        p="5"
        justifyContent="space-between"
        alignSelf="stretch"
        alignItems="center"
      >
        <Flex justify="center" align="flex-start" padding="4">
          <Image src={logoWithName} width={160} />
          <Text
            bg="accent.500"
            color="white"
            padding="0px 6px"
            fontSize="xs"
            fontWeight="500"
            borderRadius="24px"
          >
            Lab
          </Text>
        </Flex>
        <Button
          leftIcon={<LogoutIcon />}
          bgColor="white"
          variant="outline"
          color="red.500"
          onClick={() => {
            signOut();
          }}
        >
          Logout
        </Button>
      </Flex>
      <Divider />
      <Box
        p={5}
        background="white"
        w={{ base: "100%", lg: "70%" }}
        maxW="1200px"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={6}>
            <ImageInput
              alt={"Lab Image"}
              errors={errors}
              register={register}
              trigger={trigger}
            />
            <Stack direction="row">
              <FormControl id="name" isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  type="text"
                  name="name"
                  placeholder="Name"
                  ref={register}
                />
              </FormControl>
              <FormControl id="phoneNumber" isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="text"
                  name="phoneNumber"
                  placeholder="Phone Number"
                  ref={register}
                />
              </FormControl>
            </Stack>

            <FormControl id="address" isRequired>
              <FormLabel>Address</FormLabel>
              <Textarea
                type="text"
                name="address"
                placeholder="Address"
                ref={register}
              />
            </FormControl>
            <FormControl id="description" isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                type="text"
                name="description"
                placeholder="Description"
                ref={register}
              />
            </FormControl>

            <FormControl id="location">
              <FormLabel>Map</FormLabel>
              <Controller
                control={control}
                name="location"
                render={({ onChange, value }) => (
                  <Map value={value} onChange={onChange} />
                )}
              />
            </FormControl>
            <Stack direction="row">
              <FormControl id="panNumber" isRequired>
                <FormLabel>PAN Number</FormLabel>
                <Input
                  type="number"
                  name="panNumber"
                  placeholder="PAN Number"
                  ref={register}
                />
              </FormControl>
              <FormControl id="website">
                <FormLabel>Website</FormLabel>
                <Input
                  type="text"
                  name="website"
                  placeholder="Website"
                  ref={register}
                />
              </FormControl>
            </Stack>

            <Stack direction="row">
              <FormControl id="bankAccountNumber" isRequired>
                <FormLabel>Bank Account Number</FormLabel>
                <Input
                  type="text"
                  name="bankAccountNumber"
                  placeholder="Bank Account Number"
                  ref={register}
                />
              </FormControl>
              <FormControl id="bankAccountName" isRequired>
                <FormLabel>Bank Account Name</FormLabel>
                <Input
                  type="text"
                  name="bankAccountName"
                  placeholder="Bank Account Name"
                  ref={register}
                />
              </FormControl>
            </Stack>

            <Stack direction="row">
              <FormControl id="bankName" isRequired>
                <FormLabel>Bank Name</FormLabel>
                <Input
                  type="text"
                  name="bankName"
                  placeholder="Bank Name"
                  ref={register}
                />
              </FormControl>
              <FormControl id="bankBranch" isRequired>
                <FormLabel>Bank Branch</FormLabel>
                <Input
                  type="text"
                  name="bankBranch"
                  placeholder="Bank Branch"
                  ref={register}
                />
              </FormControl>
            </Stack>

            <Stack direction="row">
              <FormControl id="opensAt" isRequired>
                <FormLabel>Opening Time</FormLabel>
                <Input type="time" name="opensAt" ref={register} />
              </FormControl>
              <FormControl id="closesAt" isRequired>
                <FormLabel>Closing Time</FormLabel>
                <Input type="time" name="closesAt" ref={register} />
              </FormControl>
            </Stack>

            <FormControl id="verificationDocuments" isRequired>
              <FormLabel>Document</FormLabel>
              <input
                type="file"
                name="verificationDocuments"
                id="verificationDocuments"
                multiple
                ref={register}
              />
            </FormControl>
            <Button type="submit" isLoading={completeSignupInfo.isLoading}>
              Submit
            </Button>
          </Stack>
        </form>
      </Box>
    </Flex>
  );
}
